import { memo, useCallback } from "react";
import { useUnistyles } from "react-native-unistyles";
import * as WebBrowser from "expo-web-browser";
import { View, Text, Card, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { useUpsellStatus } from "@visioni/a3s-upsell/app";
import { logError } from "@src/features/error-logging";

interface MonitoringUpsellBannerProps {
  siteId: string;
  onUpgrade?: () => void;
}

/**
 * Per-user upsell banner for 24/7 monitoring. The component reads the
 * current upsell status from the backend and reflects one of three states:
 *
 * - `none` / loading — show the "Upgrade" CTA which opens the upgrade
 *   dialog via `onUpgrade`.
 * - `pending` — show a non-interactive "Request pending" message; the
 *   sales team has already been notified for this user.
 * - error — fall back to opening the region-appropriate contact URL so
 *   the user can still reach the team if HubSpot is unreachable.
 */
export const MonitoringUpsellBanner = memo(({ siteId, onUpgrade }: MonitoringUpsellBannerProps) => {
  const { theme } = useUnistyles();
  const { upsellStatus, isError } = useUpsellStatus({ product: "monitoring", siteId });
  const regionConfig = useRegionConfig();

  const openContactFallback = useCallback(() => {
    WebBrowser.openBrowserAsync(regionConfig.contactUrl, {
      presentationStyle: WebBrowser.WebBrowserPresentationStyle.FORM_SHEET,
    }).catch(logError);
  }, [regionConfig.contactUrl]);

  const isPending = upsellStatus === "pending";

  const handlePress = useCallback(() => {
    if (isError) {
      openContactFallback();
      return;
    }
    onUpgrade?.();
  }, [isError, openContactFallback, onUpgrade]);

  return (
    <Card
      style={{
        borderWidth: 1,
        borderColor: theme.colors.status.success,
        paddingVertical: theme.spacing.lg,
        paddingHorizontal: theme.spacing["2xl"],
      }}
    >
      <View flexDirection="row" alignItems="center" gap="sm" mb="xl">
        <Text textStyle="sm/regular">Monitoring</Text>
        <View
          flexDirection="row"
          alignItems="center"
          gap="xs"
          style={{
            backgroundColor: theme.colors.status.success,
            borderRadius: theme.spacing["3xl"],
            paddingHorizontal: theme.spacing.md,
            paddingVertical: theme.spacing.xxs,
          }}
        >
          <Icon name="crown" size={16} color={theme.colors.white.default} />
          <Text textStyle="xxs/regular" style={{ color: theme.colors.white.default }}>
            Upgrade
          </Text>
        </View>
      </View>
      <Text textStyle="xs/regular" color="textMuted" mb="3xl">
        Protect assets in real time and deploy response with Vision Intelligence 24-hour ASIAL-accredited monitoring
        service. Our highly trained in-house team know Vision Intelligence products inside and out, and are equipped to
        respond to events quickly and efficiently.
      </Text>
      {isPending ?
        <Text textStyle="xs/regular" color="textMuted">
          We&apos;ve got your request — our team will be in touch soon.
        </Text>
      : <Button
          label={isError ? "Contact us" : "Upgrade"}
          variant="primary"
          variantColor="blue"
          size="sm"
          onPress={handlePress}
        />
      }
    </Card>
  );
});
